// Aviso «Llegaste al límite de tu plan» (CC-CONSOLE #1613).
//
// El API responde 402 con `meta.code = "PLAN_LIMIT_REACHED"` cuando crear un
// usuario, un vehículo o un dispositivo rebasa la licencia de la compañía. El
// interceptor de `utils/api.ts` lo detecta y muestra este aviso con «Mejorar
// plan», que lleva a «Elige tu licencia» (/billing/plans) con los planes del
// caso de uso de la compañía. La petición se sigue rechazando: quien llamó
// decide si además muestra su propio error.

import Swal from "sweetalert2";
import i18n from "i18next";
import { DIALOG_COLORS } from "../components/ui";
import { API_ERROR_CODES, apiErrorCode } from "./apiErrorCodes";

export interface PlanLimitDetails {
  upgrade?: boolean;
  entity?: "users" | "vehicles" | "devices" | string;
  limit?: number;
  used?: number;
  licenseId?: string | null;
  licenseName?: string | null;
}

export const PLANS_PATH = "/billing/plans";

/** ¿Es el 402 de tope del plan? */
export function isPlanLimitError(error: unknown): boolean {
  return apiErrorCode(error) === API_ERROR_CODES.PLAN_LIMIT_REACHED;
}

/** `meta.details` del 402 (vacío si no viene). */
export function planLimitDetails(error: unknown): PlanLimitDetails {
  const data = (error as { response?: { data?: { meta?: { details?: unknown } } } })?.response?.data;
  const details = data?.meta?.details;
  return details && typeof details === "object" ? (details as PlanLimitDetails) : {};
}

/** Texto del aviso según la entidad (claves `useCases:limit.*`). Pura. */
export function planLimitMessage(details: PlanLimitDetails, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const entity = details.entity;
  if ((entity === "users" || entity === "vehicles" || entity === "devices") && typeof details.limit === "number") {
    return t(`useCases:limit.${entity}`, {
      plan: details.licenseName || "",
      limit: details.limit,
      interpolation: { escapeValue: false },
    });
  }
  return t("useCases:limit.fallback");
}

let showing = false;

/**
 * Muestra el aviso (uno a la vez). Con «Mejorar plan» navega a los planes.
 * Nunca lanza.
 */
export async function showPlanLimitNotice(
  error: unknown,
  navigate: (path: string) => void = (path) => window.location.assign(path),
): Promise<void> {
  if (showing) return;
  showing = true;
  try {
    const t = i18n.t.bind(i18n) as (key: string, opts?: Record<string, unknown>) => string;
    const result = await Swal.fire({
      icon: "info",
      title: t("useCases:limit.title"),
      text: planLimitMessage(planLimitDetails(error), t),
      showCancelButton: true,
      confirmButtonText: t("useCases:limit.upgrade"),
      cancelButtonText: t("useCases:limit.close"),
      confirmButtonColor: DIALOG_COLORS.success,
    });
    if (result.isConfirmed) navigate(PLANS_PATH);
  } catch {
    /* sin aviso: la petición igual se rechaza */
  } finally {
    showing = false;
  }
}
